import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box, Button, Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, CircularProgress, Chip, Grid, TextField, IconButton, Dialog,
  Tooltip, InputAdornment, LinearProgress,
} from "@mui/material";
import InventoryIcon from "@mui/icons-material/Inventory";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import HistoryIcon from "@mui/icons-material/History";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import RefreshIcon from "@mui/icons-material/Refresh";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PrintIcon from "@mui/icons-material/Print";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { Checkbox, Select, MenuItem, Skeleton } from "@mui/material";
import useBulkSelection from "../../hooks/useBulkSelection";
import BulkActionBar from "../../Components/common/BulkActionBar";

const API_URL = (process.env.REACT_APP_API_URL || "").trim();

interface Product {
  id: number; productName: string; productBarcode: string; productCategoryId: number;
  productPrice: number; salePrice: number; wholesaleprice: number;
  current_stock: number; minimum_stock: number; reorder_point: number; reorder_qty: number;
  track_inventory: boolean; qty: number; stock_status: string; needs_reorder: boolean;
}
interface Summary { total: number; tracked: number; good: number; low: number; critical: number; out_of_stock: number; needs_reorder: number; }
interface Movement { id: number; product_id: number; type: string; qty: number; stock_before: number; stock_after: number; reference: string; notes: string; created_at: string; product?: { id: number; productName: string }; }

const statusConfig: Record<string, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  good: { label: "Good", color: "#16a34a", bg: "#f0fdf4", icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
  low: { label: "Low", color: "#ea580c", bg: "#fff7ed", icon: <WarningAmberIcon sx={{ fontSize: 14 }} /> },
  critical: { label: "Critical", color: "#dc2626", bg: "#fef2f2", icon: <ErrorIcon sx={{ fontSize: 14 }} /> },
  out: { label: "Out", color: "#7f1d1d", bg: "#fef2f2", icon: <ErrorIcon sx={{ fontSize: 14 }} /> },
  untracked: { label: "Untracked", color: "#94a3b8", bg: "#f8fafc", icon: <InventoryIcon sx={{ fontSize: 14 }} /> },
};

const thSx = { fontWeight: 600, fontSize: 10, color: "#94a3b8", textTransform: "uppercase" as const, letterSpacing: "0.08em", py: 1.5, borderBottom: "1px solid #f1f5f9", bgcolor: "#fafbfc" };

const Warehouse: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editProduct, setEditProduct] = useState<Product | null>(null);
  const [editForm, setEditForm] = useState({ current_stock: 0, minimum_stock: 0, reorder_point: 0, reorder_qty: 0, track_inventory: true });
  const [saving, setSaving] = useState(false);
  const [adjustProduct, setAdjustProduct] = useState<Product | null>(null);
  const [adjustQty, setAdjustQty] = useState("");
  const [adjustType, setAdjustType] = useState<"add" | "deduct">("add");
  const [adjustNotes, setAdjustNotes] = useState("");
  const [movements, setMovements] = useState<Movement[]>([]);
  const [movementProduct, setMovementProduct] = useState<Product | null>(null);
  const [movementsLoading, setMovementsLoading] = useState(false);

  const getHeaders = () => {
    const t = localStorage.getItem("token") || "";
    return { "Content-Type": "application/json", Accept: "application/json", Authorization: `Bearer ${t}` };
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/warehouse/dashboard`, {
        method: "POST", headers: getHeaders(),
        body: JSON.stringify({ search: "", status: statusFilter, track_only: false }),
      });
      const json = await res.json();
      if (json.success) { setProducts(json.data || []); setSummary(json.summary || null); }
    } catch {} finally { setLoading(false); }
  }, [statusFilter]);

  // Client-side, case-insensitive filter on product name + barcode
  const filteredProducts = useMemo(() => {
    const term = (search || "").trim().toLowerCase();
    if (!term) return products;
    return products.filter(p =>
      (p.productName || "").toLowerCase().includes(term) ||
      (p.productBarcode || "").toLowerCase().includes(term)
    );
  }, [products, search]);

  // Recompute summary from the visible (filtered) set so cards reflect search
  const filteredSummary: Summary | null = useMemo(() => {
    if (!products.length) return summary;
    const tracked = filteredProducts.filter(p => p.track_inventory);
    return {
      total: filteredProducts.length,
      tracked: tracked.length,
      good: filteredProducts.filter(p => p.stock_status === "good").length,
      low: filteredProducts.filter(p => p.stock_status === "low").length,
      critical: filteredProducts.filter(p => p.stock_status === "critical").length,
      out_of_stock: filteredProducts.filter(p => p.stock_status === "out").length,
      needs_reorder: filteredProducts.filter(p => p.needs_reorder).length,
    };
  }, [filteredProducts, products.length, summary]);

  // Tracked filter (client-side, layered on top)
  const [trackedFilter, setTrackedFilter] = useState<"" | "tracked" | "untracked">("");
  const visibleProducts = useMemo(() => {
    if (!trackedFilter) return filteredProducts;
    return filteredProducts.filter(p =>
      trackedFilter === "tracked" ? p.track_inventory : !p.track_inventory
    );
  }, [filteredProducts, trackedFilter]);

  // Bulk selection
  const bulk = useBulkSelection<Product>(visibleProducts);

  const resetFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setTrackedFilter("");
  };

  // Inline +/- adjust → POST /inventory/adjust
  const adjustStockInline = async (productId: number, qty: number) => {
    try {
      const res = await fetch(`${API_URL}/inventory/adjust`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({ product_id: productId, quantity: qty }),
      });
      const json = await res.json();
      if (json.status || json.success) load();
    } catch {}
  };

  // Reorder → navigate to PO create with prefilled supplier/qty (or new PO page)
  const handleReorder = (p: Product) => {
    const qty = p.reorder_qty || Math.max(1, p.minimum_stock || 10);
    const params = new URLSearchParams({
      product_id: String(p.id),
      qty: String(qty),
      product_name: p.productName,
    });
    window.open(`/bill/create?${params.toString()}`, "_blank");
  };

  // Bulk delete (uses the inventory bulk-delete endpoint)
  const handleBulkDelete = async () => {
    if (!bulk.selectedIds.length) return;
    if (!window.confirm(`Delete ${bulk.selectedIds.length} selected products from warehouse?`)) return;
    try {
      const res = await fetch(`${API_URL}/inventory/bulk-delete`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({ ids: bulk.selectedIds }),
      });
      const json = await res.json();
      if (json.status || json.success) {
        bulk.clearSelection();
        load();
      }
    } catch {}
  };

  // Export visible rows → CSV
  const handleExport = () => {
    const rows = visibleProducts.map((p, i) => ({
      "#": i + 1,
      Product: p.productName,
      Barcode: p.productBarcode,
      Status: p.stock_status,
      "Current Stock": p.current_stock,
      "Min Stock": p.minimum_stock,
      "Reorder Point": p.reorder_point,
      "Reorder Qty": p.reorder_qty,
      Tracked: p.track_inventory ? "Yes" : "No",
    }));
    const headerKeys = Object.keys(rows[0] || { Product: "" });
    const csv = [
      headerKeys.join(","),
      ...rows.map((r) => headerKeys.map((k) => `"${String((r as any)[k] ?? "").replace(/"/g, '""')}"`).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `warehouse-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => window.print();

  useEffect(() => { load(); }, [load]);

  const handleSaveSettings = async () => {
    if (!editProduct) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/warehouse/product/${editProduct.id}/settings`, {
        method: "PUT", headers: getHeaders(), body: JSON.stringify(editForm),
      });
      const json = await res.json();
      if (json.success) { setEditProduct(null); load(); }
    } catch {} finally { setSaving(false); }
  };

  const handleAdjust = async () => {
    if (!adjustProduct || !adjustQty) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/warehouse/product/${adjustProduct.id}/adjust`, {
        method: "POST", headers: getHeaders(),
        body: JSON.stringify({ qty: Number(adjustQty), type: adjustType, notes: adjustNotes }),
      });
      const json = await res.json();
      if (json.success) { setAdjustProduct(null); setAdjustQty(""); setAdjustNotes(""); load(); }
    } catch {} finally { setSaving(false); }
  };

  const loadMovements = async (product: Product) => {
    setMovementProduct(product); setMovementsLoading(true);
    try {
      const res = await fetch(`${API_URL}/warehouse/movements/${product.id}`, { headers: getHeaders() });
      const json = await res.json();
      if (json.success) setMovements(json.data || []);
    } catch {} finally { setMovementsLoading(false); }
  };

  const openEdit = (p: Product) => {
    setEditForm({ current_stock: p.current_stock, minimum_stock: p.minimum_stock, reorder_point: p.reorder_point, reorder_qty: p.reorder_qty, track_inventory: p.track_inventory });
    setEditProduct(p);
  };

  const stockPct = (p: Product) => {
    if (!p.track_inventory || p.reorder_point <= 0) return 100;
    return Math.min(100, Math.round((p.current_stock / (p.reorder_point * 2)) * 100));
  };

  return (
    <Box sx={{
      p: 3, minHeight: "100vh",
      background: "linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)",
    }}>
      <Box sx={{ maxWidth: 1440, mx: "auto" }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box display="flex" alignItems="center" gap={2}>
          <Box sx={{
            width: 48, height: 48, borderRadius: "12px",
            background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <InventoryIcon sx={{ fontSize: 24, color: "#fff" }} />
          </Box>
          <Box>
            <Typography fontWeight={700} fontSize={22} color="#0f172a">Warehouse Inventory</Typography>
            <Typography fontSize={13} color="#64748b">Smart stock monitoring & control</Typography>
          </Box>
        </Box>
        <Box display="flex" gap={1}>
          <Button variant="outlined" startIcon={<FileDownloadIcon sx={{ fontSize: 16 }} />} onClick={handleExport}
            sx={{ borderRadius: "8px", textTransform: "none", fontSize: 12, fontWeight: 600, borderColor: "#E5E7EB", color: "#0f172a", "&:hover": { borderColor: "#0f172a", bgcolor: "#F8FAFC" } }}>
            Export
          </Button>
          <Button variant="outlined" startIcon={<PrintIcon sx={{ fontSize: 16 }} />} onClick={handlePrint}
            sx={{ borderRadius: "8px", textTransform: "none", fontSize: 12, fontWeight: 600, borderColor: "#E5E7EB", color: "#0f172a", "&:hover": { borderColor: "#0f172a", bgcolor: "#F8FAFC" } }}>
            Print
          </Button>
          <Button variant="outlined" startIcon={<RefreshIcon sx={{ fontSize: 16 }} />} onClick={load}
            sx={{ borderRadius: "8px", textTransform: "none", fontSize: 12, fontWeight: 600, borderColor: "#E5E7EB", color: "#0f172a", "&:hover": { borderColor: "#0f172a", bgcolor: "#F8FAFC" } }}>
            Refresh
          </Button>
          <Button variant="contained" disableElevation startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            onClick={() => products[0] && setAdjustProduct(products[0])}
            sx={{ borderRadius: "8px", textTransform: "none", fontSize: 12, fontWeight: 600, bgcolor: "#0f172a", "&:hover": { bgcolor: "#1e293b" } }}>
            Adjust Stock
          </Button>
        </Box>
      </Box>

      {/* Summary Cards — clickable filters */}
      {filteredSummary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: "Total Products", value: filteredSummary.total, filter: "all", color: "#0f172a" },
            { label: "Tracked", value: filteredSummary.tracked, filter: "tracked", color: "#7c3aed" },
            { label: "Good Stock", value: filteredSummary.good, filter: "good", color: "#16a34a" },
            { label: "Low Stock", value: filteredSummary.low, filter: "low", color: "#ea580c" },
            { label: "Critical", value: filteredSummary.critical, filter: "critical", color: "#dc2626" },
            { label: "Needs Reorder", value: filteredSummary.needs_reorder, filter: "reorder", color: "#0f172a" },
          ].map((c) => {
            const isActive = statusFilter === c.filter || (statusFilter === "all" && c.filter === "all");
            return (
              <Grid item xs={6} sm={4} md={2} key={c.filter}>
                <Paper elevation={0} onClick={() => setStatusFilter(c.filter === statusFilter ? "all" : c.filter)}
                  sx={{
                    borderRadius: "14px", cursor: "pointer",
                    bgcolor: "#ffffff",
                    border: isActive ? `1px solid ${c.color}` : "1px solid #e2e8f0",
                    overflow: "hidden",
                    transition: "transform 0.2s ease, border-color 0.2s ease",
                    "&:hover": { transform: "translateY(-2px)", borderColor: "#0f172a" },
                  }}>
                  <Box sx={{ height: 4, bgcolor: c.color }} />
                  <Box sx={{ p: 2 }}>
                    <Typography fontSize={11} color="#64748b" fontWeight={600} sx={{ textTransform: "uppercase", letterSpacing: "0.06em" }}>{c.label}</Typography>
                    <Typography fontSize={24} fontWeight={700} color="#0f172a" letterSpacing="-0.02em" mt={0.5}>{c.value}</Typography>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Filter bar */}
      <Box display="flex" gap={1.5} mb={2} flexWrap="wrap" alignItems="center">
        <TextField size="small" placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} /></InputAdornment> }}
          sx={{ minWidth: 300, bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: "12px", fontSize: 13, bgcolor: "#fff", "& fieldset": { borderColor: "#e2e8f0" }, "&:hover fieldset": { borderColor: "#cbd5e1" }, "&.Mui-focused fieldset": { borderColor: "#0f172a", borderWidth: "1.5px" } } }} />
        <Select size="small" value={statusFilter} onChange={e => setStatusFilter(e.target.value as string)}
          sx={{ minWidth: 160, borderRadius: "10px", fontSize: 13, bgcolor: "#fff", "& fieldset": { borderColor: "#E5E7EB" } }}>
          <MenuItem value="all">All Status</MenuItem>
          <MenuItem value="good">Good</MenuItem>
          <MenuItem value="low">Low</MenuItem>
          <MenuItem value="critical">Critical</MenuItem>
          <MenuItem value="out">Out of Stock</MenuItem>
          <MenuItem value="reorder">Needs Reorder</MenuItem>
        </Select>
        <Select size="small" value={trackedFilter} onChange={e => setTrackedFilter(e.target.value as any)} displayEmpty
          sx={{ minWidth: 150, borderRadius: "10px", fontSize: 13, bgcolor: "#fff", "& fieldset": { borderColor: "#E5E7EB" } }}>
          <MenuItem value="">All Items</MenuItem>
          <MenuItem value="tracked">Tracked</MenuItem>
          <MenuItem value="untracked">Untracked</MenuItem>
        </Select>
        {(search || statusFilter !== "all" || trackedFilter) && (
          <Button size="small" variant="outlined" startIcon={<RestartAltIcon sx={{ fontSize: 16 }} />} onClick={resetFilters}
            sx={{ borderRadius: "10px", textTransform: "none", fontSize: 12, fontWeight: 600, borderColor: "#E5E7EB", color: "#0f172a", height: 40, "&:hover": { borderColor: "#0f172a", bgcolor: "#f8fafc" } }}>
            Reset
          </Button>
        )}
        {(search || statusFilter !== "all" || trackedFilter) && (
          <Typography fontSize={12} color="#64748b" sx={{ ml: 0.5 }}>
            Showing {visibleProducts.length} of {products.length}
          </Typography>
        )}
      </Box>

      {/* Bulk action bar */}
      <BulkActionBar
        selectedCount={bulk.selectedIds.length}
        onClear={bulk.clearSelection}
        onDelete={handleBulkDelete}
      />

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius: "14px", border: "1px solid #e2e8f0", overflow: "hidden", bgcolor: "#fff" }}>
        {loading ? (
          <Box sx={{ p: 2 }}>
            <Skeleton variant="rectangular" height={48} sx={{ borderRadius: "10px", mb: 1, bgcolor: "#0f172a", opacity: 0.08 }} />
            {[...Array(8)].map((_, i) => (
              <Skeleton key={i} variant="rectangular" height={56} sx={{ borderRadius: "8px", mb: 0.75 }} />
            ))}
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: "calc(100vh - 360px)", borderRadius: "14px" }}>
            <Table size="small" sx={{
              "& .MuiTableHead-root .MuiTableRow-root": { backgroundColor: "#0f172a" },
              "& .MuiTableHead-root .MuiTableCell-root": {
                backgroundColor: "#0f172a", backgroundImage: "none",
                color: "#fff", fontSize: 10.5, fontWeight: 700,
                textTransform: "uppercase", letterSpacing: "0.06em",
                borderBottom: "none", boxShadow: "none", py: 1.5,
              },
            }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40 }}>
                    <Checkbox size="small" checked={bulk.allSelected} indeterminate={bulk.indeterminate}
                      onChange={() => bulk.toggleAll(visibleProducts)}
                      sx={{ color: "#fff", "&.Mui-checked": { color: "#fff" }, "&.MuiCheckbox-indeterminate": { color: "#fff" } }} />
                  </TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="right">Current</TableCell>
                  <TableCell align="right">Min</TableCell>
                  <TableCell align="right">Reorder At</TableCell>
                  <TableCell align="right">Reorder Qty</TableCell>
                  <TableCell align="center">Stock Level</TableCell>
                  <TableCell align="center">Quick Adjust</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleProducts.map(p => {
                  const sc = statusConfig[p.stock_status] || statusConfig.untracked;
                  const pct = stockPct(p);
                  const barColor = pct >= 50 ? "#22c55e" : pct >= 20 ? "#f59e0b" : "#ef4444";
                  return (
                    <TableRow key={p.id} sx={{ "& td": { borderBottom: "1px solid #F1F5F9" }, "&:hover td": { bgcolor: "#F6F8FA" }, transition: "background 0.15s", bgcolor: p.needs_reorder ? "#FFFBEB" : "inherit" }}>
                      <TableCell sx={{ width: 40 }}>
                        <Checkbox size="small" checked={bulk.isSelected(p.id)} onChange={() => bulk.toggleOne(p.id)} />
                      </TableCell>
                      <TableCell sx={{ py: 1.25 }}>
                        <Typography fontSize={13} fontWeight={600} color="#0f172a" sx={{ textTransform: "capitalize" }}>{p.productName}</Typography>
                        {p.productBarcode && <Typography fontSize={10} color="#94a3b8">{p.productBarcode}</Typography>}
                      </TableCell>
                      <TableCell align="center" sx={{ py: 1.25 }}>
                        <Chip icon={sc.icon as React.ReactElement} label={sc.label} size="small"
                          sx={{ height: 22, fontSize: 10, fontWeight: 700, bgcolor: sc.bg, color: sc.color, borderRadius: "999px", "& .MuiChip-icon": { color: sc.color } }} />
                      </TableCell>
                      <TableCell align="right" sx={{ py: 1.25 }}>
                        <Typography fontSize={15} fontWeight={700} color={p.current_stock <= 0 ? "#dc2626" : "#0f172a"} sx={{ fontVariantNumeric: "tabular-nums" }}>
                          {p.track_inventory ? p.current_stock : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 1.25 }}>
                        <Typography fontSize={12} color="#64748b" sx={{ fontVariantNumeric: "tabular-nums" }}>{p.track_inventory ? p.minimum_stock : "—"}</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 1.25 }}>
                        <Typography fontSize={12} color="#64748b" sx={{ fontVariantNumeric: "tabular-nums" }}>{p.track_inventory ? p.reorder_point : "—"}</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 1.25 }}>
                        <Typography fontSize={12} color="#64748b" sx={{ fontVariantNumeric: "tabular-nums" }}>{p.track_inventory ? p.reorder_qty : "—"}</Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 1.25, width: 130 }}>
                        {p.track_inventory ? (
                          <Tooltip title={`${pct}% of safe level`}>
                            <Box>
                              <Box sx={{ width: "100%", height: 6, bgcolor: "#e2e8f0", borderRadius: 4, overflow: "hidden" }}>
                                <Box sx={{ width: `${pct}%`, height: "100%", borderRadius: 4, bgcolor: barColor, transition: "width 0.3s" }} />
                              </Box>
                              <Typography fontSize={9} color="#94a3b8" mt={0.25}>{pct}%</Typography>
                            </Box>
                          </Tooltip>
                        ) : <Typography fontSize={10} color="#cbd5e1">N/A</Typography>}
                      </TableCell>
                      <TableCell align="center" sx={{ py: 1.25, width: 100 }}>
                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                          <Tooltip title="Decrease by 1">
                            <span>
                              <IconButton size="small" onClick={() => adjustStockInline(p.id, -1)}
                                sx={{ width: 28, height: 28, borderRadius: "8px", bgcolor: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca", "&:hover": { bgcolor: "#fee2e2" } }}>
                                <RemoveIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Increase by 1">
                            <span>
                              <IconButton size="small" onClick={() => adjustStockInline(p.id, +1)}
                                sx={{ width: 28, height: 28, borderRadius: "8px", bgcolor: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0", "&:hover": { bgcolor: "#dcfce7" } }}>
                                <AddIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 1.25 }}>
                        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center", alignItems: "center" }}>
                          <Tooltip title="Edit Settings"><IconButton size="small" onClick={() => openEdit(p)}
                            sx={{ width: 28, height: 28, borderRadius: "8px", bgcolor: "#f8fafc", color: "#0f172a", border: "1px solid #e2e8f0", "&:hover": { bgcolor: "#e2e8f0" } }}>
                            <EditIcon sx={{ fontSize: 14 }} />
                          </IconButton></Tooltip>
                          <Tooltip title="Stock History"><IconButton size="small" onClick={() => loadMovements(p)}
                            sx={{ width: 28, height: 28, borderRadius: "8px", bgcolor: "#f8fafc", color: "#0f172a", border: "1px solid #e2e8f0", "&:hover": { bgcolor: "#e2e8f0" } }}>
                            <HistoryIcon sx={{ fontSize: 14 }} />
                          </IconButton></Tooltip>
                          {p.needs_reorder && (
                            <Tooltip title="Create Purchase Order">
                              <Button size="small" variant="contained" disableElevation
                                startIcon={<LocalShippingIcon sx={{ fontSize: 13 }} />}
                                onClick={() => handleReorder(p)}
                                sx={{ borderRadius: "8px", textTransform: "none", fontSize: 11, fontWeight: 600, py: 0.25, px: 1, bgcolor: "#0f172a", "&:hover": { bgcolor: "#1e293b" }, minWidth: 0 }}>
                                Reorder
                              </Button>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {visibleProducts.length === 0 && (
                  <TableRow><TableCell colSpan={10} sx={{ textAlign: "center", py: 8 }}>
                    <InventoryIcon sx={{ fontSize: 40, color: "#e5e7eb", mb: 1 }} />
                    <Typography fontSize={14} fontWeight={600} color="#64748b">No inventory found</Typography>
                    <Typography fontSize={12} color="#94a3b8" mt={0.5}>
                      {(search || statusFilter !== "all" || trackedFilter) ? "Try adjusting or resetting filters" : "Inventory will appear once products are tracked"}
                    </Typography>
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      </Box>

      {/* Edit Settings Dialog */}
      <Dialog open={!!editProduct} onClose={() => setEditProduct(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5 }}>
            <Typography fontSize={16} fontWeight={700} color="#0f172a">Inventory Settings</Typography>
            <IconButton size="small" onClick={() => setEditProduct(null)}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
          </Box>
          {editProduct && <Typography fontSize={13} color="#64748b" mb={2} sx={{ textTransform: "capitalize" }}>{editProduct.productName}</Typography>}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography fontSize={12} fontWeight={600} color="#64748b">Track Inventory</Typography>
              <Chip label={editForm.track_inventory ? "ON" : "OFF"} size="small"
                onClick={() => setEditForm(f => ({ ...f, track_inventory: !f.track_inventory }))}
                sx={{ height: 24, fontSize: 11, fontWeight: 700, cursor: "pointer", bgcolor: editForm.track_inventory ? "#f0fdf4" : "#f8fafc", color: editForm.track_inventory ? "#16a34a" : "#94a3b8" }} />
            </Box>
            {[
              { key: "current_stock", label: "Current Stock" },
              { key: "minimum_stock", label: "Minimum Stock" },
              { key: "reorder_point", label: "Reorder Point" },
              { key: "reorder_qty", label: "Reorder Quantity" },
            ].map(f => (
              <TextField key={f.key} size="small" label={f.label} type="number"
                value={(editForm as any)[f.key]} onChange={e => setEditForm(prev => ({ ...prev, [f.key]: Number(e.target.value) }))}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
            ))}
            <Button variant="contained" disableElevation onClick={handleSaveSettings} disabled={saving}
              startIcon={saving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <SaveIcon sx={{ fontSize: 16 }} />}
              sx={{ borderRadius: 2.5, py: 1, fontWeight: 700, bgcolor: "#0f172a", "&:hover": { bgcolor: "#1e293b" }, textTransform: "none" }}>
              Save Settings
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Adjust Stock Dialog */}
      <Dialog open={!!adjustProduct} onClose={() => setAdjustProduct(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Typography fontSize={16} fontWeight={700} color="#0f172a">Adjust Stock</Typography>
            <IconButton size="small" onClick={() => setAdjustProduct(null)}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
          </Box>
          {adjustProduct && (
            <>
              <Typography fontSize={13} color="#64748b" mb={0.5} sx={{ textTransform: "capitalize" }}>{adjustProduct.productName}</Typography>
              <Typography fontSize={12} color="#94a3b8" mb={2}>Current: <strong>{adjustProduct.current_stock}</strong></Typography>
              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <Chip label="Add Stock" size="small" onClick={() => setAdjustType("add")}
                  sx={{ cursor: "pointer", fontWeight: 600, bgcolor: adjustType === "add" ? "#f0fdf4" : "#f8fafc", color: adjustType === "add" ? "#16a34a" : "#94a3b8", border: `1px solid ${adjustType === "add" ? "#bbf7d0" : "rgba(0,0,0,0.06)"}` }} />
                <Chip label="Deduct Stock" size="small" onClick={() => setAdjustType("deduct")}
                  sx={{ cursor: "pointer", fontWeight: 600, bgcolor: adjustType === "deduct" ? "#fef2f2" : "#f8fafc", color: adjustType === "deduct" ? "#dc2626" : "#94a3b8", border: `1px solid ${adjustType === "deduct" ? "#fecaca" : "rgba(0,0,0,0.06)"}` }} />
              </Box>
              <TextField size="small" fullWidth label="Quantity" type="number" value={adjustQty} onChange={e => setAdjustQty(e.target.value)}
                sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
              <TextField size="small" fullWidth label="Notes (optional)" value={adjustNotes} onChange={e => setAdjustNotes(e.target.value)}
                sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
              <Button variant="contained" disableElevation fullWidth onClick={handleAdjust} disabled={saving || !adjustQty}
                sx={{ borderRadius: 2.5, py: 1, fontWeight: 700, bgcolor: adjustType === "add" ? "#16a34a" : "#dc2626", "&:hover": { bgcolor: adjustType === "add" ? "#15803d" : "#b91c1c" }, textTransform: "none" }}>
                {saving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : `${adjustType === "add" ? "Add" : "Deduct"} ${adjustQty || 0} units`}
              </Button>
            </>
          )}
        </Box>
      </Dialog>

      {/* Movements Dialog */}
      <Dialog open={!!movementProduct} onClose={() => setMovementProduct(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Typography fontSize={16} fontWeight={700} color="#0f172a">Stock History</Typography>
            <IconButton size="small" onClick={() => setMovementProduct(null)}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
          </Box>
          {movementProduct && <Typography fontSize={13} color="#64748b" mb={2} sx={{ textTransform: "capitalize" }}>{movementProduct.productName}</Typography>}
          {movementsLoading ? (
            <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress size={24} /></Box>
          ) : movements.length === 0 ? (
            <Typography fontSize={13} color="#94a3b8" textAlign="center" py={4}>No stock movements recorded</Typography>
          ) : (
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase" }}>Date</TableCell>
                    <TableCell sx={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase" }}>Type</TableCell>
                    <TableCell sx={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase" }} align="right">Qty</TableCell>
                    <TableCell sx={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase" }} align="right">After</TableCell>
                    <TableCell sx={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase" }}>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {movements.map(m => (
                    <TableRow key={m.id}>
                      <TableCell sx={{ fontSize: 11, py: 0.75 }}>{new Date(m.created_at).toLocaleDateString()}</TableCell>
                      <TableCell sx={{ py: 0.75 }}>
                        <Chip label={m.type.replace("_", " ")} size="small"
                          sx={{ height: 20, fontSize: 9, fontWeight: 700, textTransform: "capitalize",
                            bgcolor: m.qty > 0 ? "#f0fdf4" : "#fef2f2", color: m.qty > 0 ? "#16a34a" : "#dc2626" }} />
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.75 }}>
                        <Typography fontSize={12} fontWeight={700} color={m.qty > 0 ? "#16a34a" : "#dc2626"}>
                          {m.qty > 0 ? "+" : ""}{m.qty}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 12, py: 0.75, fontWeight: 600 }}>{m.stock_after}</TableCell>
                      <TableCell sx={{ fontSize: 11, color: "#94a3b8", py: 0.75 }}>{m.reference || m.notes || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Dialog>
    </Box>
  );
};

export default Warehouse;
